import { upsertProgramFixedContentRows } from '../services/programFixedContentService';
import { upsertProgramProgressCells } from '../services/programProgressService';
import type { ProgramScopedContentRowFieldPatch } from '../services/programScopedContentService';
import { saveScopedRecordFieldsAtPosition } from './scopedRecordSave';
import { clearProgramLessonRecordCaches } from './annualProgressClientCacheInvalidation';
import { emitProgramProgressSaved } from './programProgressSavedEvents';
import { readProgramFixedRowsCacheOrQuery } from '../query/readSchoolQueryCache';
import type {
  AnnualProgressProgramRecordPatch,
  AnnualProgressProgramRecordTarget,
} from './annualProgressProgramRecordTarget';

export type {
  AnnualProgressProgramRecordPatch,
  AnnualProgressProgramRecordTarget,
} from './annualProgressProgramRecordTarget';
export {
  buildFixedRecordTarget,
  buildLegacyRecordTarget,
  buildScopedRecordTarget,
  parseEvaluationMemoStorage,
  resolveAnnualProgressRecordTargets,
  serializeEvaluationMemoStorage,
} from './annualProgressProgramRecordTarget';
export { serializeClassRecordCellStorage } from './programCellDisplayText';

export async function saveAnnualProgressProgramRecord(args: {
  workspaceId: string;
  gradeId: string;
  academicYear: number;
  termNumber: 1 | 2;
  annualCellKey: string;
  target: AnnualProgressProgramRecordTarget;
  patch: AnnualProgressProgramRecordPatch;
}): Promise<void> {
  const { workspaceId, gradeId, academicYear, termNumber, annualCellKey, target, patch } = args;
  if (!workspaceId || !gradeId) {
    throw new Error('워크스페이스 또는 클래스 정보가 없습니다.');
  }

  if (target.kind === 'fixed' && target.fixedRowId) {
    const fixedCache = readProgramFixedRowsCacheOrQuery(workspaceId, academicYear, termNumber, gradeId);
    const existingRow = fixedCache.found
      ? fixedCache.rows.find((r) => r.id === target.fixedRowId) ?? null
      : null;
    if (!existingRow) {
      throw new Error('해당 수업의 고정 프로그램 원본을 찾지 못했습니다.');
    }
    await upsertProgramFixedContentRows([
      {
        id: existingRow.id,
        workspace_id: workspaceId,
        grade_id: gradeId,
        academic_year: academicYear,
        term_number: termNumber,
        event_name: existingRow.event_name,
        order_index: existingRow.order_index,
        source_annual_cell_key: existingRow.source_annual_cell_key,
        timetable_text: existingRow.timetable_text,
        unit_text: existingRow.unit_text,
        learning_text: existingRow.learning_text,
        page_text: existingRow.page_text,
        assistant_page_text: existingRow.assistant_page_text,
        current_period_text: existingRow.current_period_text,
        total_period_text: existingRow.total_period_text,
        material_text: existingRow.material_text,
        record_cross_curricular_text: patch.record_cross_curricular_text ?? existingRow.record_cross_curricular_text,
        record_memo_text: existingRow.record_memo_text,
        record_evaluation_text: patch.record_evaluation_text ?? existingRow.record_evaluation_text,
        record_class_text: patch.record_class_text ?? existingRow.record_class_text,
        record_member_group_id:
          patch.record_member_group_id !== undefined
            ? patch.record_member_group_id
            : existingRow.record_member_group_id ?? null,
        bg_color_by_field: existingRow.bg_color_by_field ?? {},
      },
    ]);
    clearProgramLessonRecordCaches(workspaceId, academicYear, termNumber, gradeId, { type: 'fixed' });
    emitProgramProgressSaved({ workspaceId, academicYear, termNumber, gradeId, uiRefreshMode: 'cache' });
    return;
  }

  if (target.kind === 'scoped' && target.scopedScopeType && target.scopedScopeKey) {
    const targetScopeKey = target.scopedScopeKey;
    const targetScopeType = target.scopedScopeType;
    /** 행 id → 없으면 스코프 안 위치(몇 번째 수업)로 찾거나 만든 뒤, 기록 칸만 고친다 */
    const fields: ProgramScopedContentRowFieldPatch = {};
    if (patch.record_cross_curricular_text !== undefined) {
      fields.record_cross_curricular_text = patch.record_cross_curricular_text;
    }
    if (patch.record_evaluation_text !== undefined) fields.record_evaluation_text = patch.record_evaluation_text;
    if (patch.record_class_text !== undefined) fields.record_class_text = patch.record_class_text;
    if (patch.record_member_group_id !== undefined) fields.record_member_group_id = patch.record_member_group_id;
    await saveScopedRecordFieldsAtPosition(
      { workspaceId, academicYear, termNumber, gradeId, scopeType: targetScopeType, scopeKey: targetScopeKey },
      { rowId: target.scopedRowId, position: target.scopedOrderIndex },
      fields
    );
    clearProgramLessonRecordCaches(workspaceId, academicYear, termNumber, gradeId, {
      type: 'scoped',
      scopeType: targetScopeType,
      scopeKey: targetScopeKey,
    });
    emitProgramProgressSaved({ workspaceId, academicYear, termNumber, gradeId, uiRefreshMode: 'cache' });
    return;
  }

  if (target.kind === 'legacy' && target.legacyCell) {
    const pc = target.legacyCell;
    await upsertProgramProgressCells([
      {
        id: pc.id,
        workspace_id: workspaceId,
        grade_id: gradeId,
        academic_year: academicYear,
        term_number: termNumber,
        monday_iso: pc.monday_iso,
        weekday_key: pc.weekday_key,
        slot_index: pc.slot_index,
        timetable_text: pc.timetable_text,
        unit_text: pc.unit_text,
        learning_text: pc.learning_text,
        page_text: pc.page_text,
        assistant_page_text: pc.assistant_page_text,
        current_period_text: pc.current_period_text,
        total_period_text: pc.total_period_text,
        material_text: pc.material_text,
        record_cross_curricular_text: patch.record_cross_curricular_text ?? pc.record_cross_curricular_text,
        record_memo_text: pc.record_memo_text,
        record_evaluation_text: patch.record_evaluation_text ?? pc.record_evaluation_text,
        record_class_text: patch.record_class_text ?? pc.record_class_text,
        record_member_group_id:
          patch.record_member_group_id !== undefined
            ? patch.record_member_group_id
            : pc.record_member_group_id ?? null,
        bg_color_by_field: pc.bg_color_by_field ?? {},
      },
    ]);
    emitProgramProgressSaved({ workspaceId, academicYear, termNumber, gradeId, uiRefreshMode: 'full' });
    return;
  }

  throw new Error('해당 수업의 프로그램 원본 데이터가 없습니다. 프로그램 페이지에서 먼저 데이터를 만들어 주세요.');
}
